import { useStore } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';

export default function ReducerInit({ reducers = {}, sagas = {}, fallback = null, children }) {
  const store = useStore();
  const [ready, setReady] = useState(false);

  const reducerKeys = useMemo(() => Object.keys(reducers), [reducers]);
  const sagaKeys = useMemo(() => Object.keys(sagas), [sagas]);

  // Helpers to detect whether things are already loaded
  const isReducerLoaded = (key) => {
    // registry check
    if (store.asyncReducers && store.asyncReducers[key]) return true;
    // state presence check
    const state = store.getState?.();
    return !!state && Object.prototype.hasOwnProperty.call(state, key);
  };

  const isSagaLoaded = (key) => {
    const reg = store.injectedSagas;
    const task = reg && reg[key];
    if (!task) return false;
    // redux-saga Task has isRunning() in standard impl
    if (typeof task.isRunning === 'function') return task.isRunning();
    return true;
  };

  useEffect(() => {
    // Ensure registries exist
    if (!store.asyncReducers) store.asyncReducers = {};
    if (!store.injectedSagas) store.injectedSagas = {};

    // Inject reducers
    if (store.injectReducer) {
      reducerKeys.forEach((key) => {
        if (!isReducerLoaded(key)) {
          store.injectReducer(key, reducers[key]);
        }
      });
    }

    // Inject/start sagas
    if (store.injectSaga) {
      sagaKeys.forEach((key) => {
        if (!isSagaLoaded(key)) {
          const task = store.injectSaga(key, sagas[key]);
          // Mirror into registry if injectSaga didn’t
          if (!store.injectedSagas[key]) store.injectedSagas[key] = task || true;
        }
      });
    }

    // Mark ready after reducer replacement completes
    const defer = (typeof queueMicrotask === 'function')
      ? queueMicrotask
      : (fn) => Promise.resolve().then(fn);
    defer(() => setReady(true));
  }, [store, reducerKeys, sagaKeys, reducers, sagas]);

  // checks before rendering
  const reducersLoaded = reducerKeys.every(isReducerLoaded);
  const sagasLoaded = sagaKeys.every((k) => isSagaLoaded(k));

  if (!ready || !reducersLoaded || !sagasLoaded) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}
